'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PasswordInput,
} from '@workspace/ui';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import posthog from 'posthog-js';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { login } from '@/actions/mixed/auth';
import { DontHaveAnAccountYet } from '@/components/auth/dont-have-an-account-yet';
import { buildAppRedirectUrl } from '@/lib/get-redirect-url';

export default function LoginForm({
  redirectToPage,
}: {
  redirectToPage: string | null | undefined;
}) {
  const t = useTranslations('auth');
  const tRoot = useTranslations();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const loginSchema = z.object({
    email: z
      .string()
      .trim()
      .email({ message: tRoot('form.emailInvalid') }),
    password: z
      .string()
      .trim()
      .min(1, { message: t('login.validation.passwordRequired') })
      .min(6, { message: t('login.validation.passwordMinLength') }),
  });

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onTouched',
  });

  async function onSubmit(values: z.infer<typeof loginSchema>) {
    try {
      setIsLoading(true);

      const result = await login(values.email.trim(), values.password.trim());

      if (result.requires2FA) {
        router.replace('/two-factor-auth');

        return;
      }

      const feAppUrl = result.fe_app_url;

      if (!feAppUrl) {
        throw new Error('Invalid response from server');
      }

      posthog.identify(values.email.trim(), { email: values.email.trim() });
      posthog.capture('user_logged_in', { email: values.email.trim() });

      window.location.href = buildAppRedirectUrl(feAppUrl, redirectToPage ?? undefined);
    } catch (err) {
      if (err instanceof Error && 'statusCode' in err && err.statusCode === 403) {
        toast.error(t('common.errors.noPermission'));
        posthog.capture('login_failed', { reason: 'no_permission' });
        setIsLoading(false);
        return;
      }

      const errorMessage = err instanceof Error ? err.message : t('common.errors.loginFailed');
      toast.error(errorMessage);
      posthog.capture('login_failed', { reason: errorMessage });
      posthog.captureException(err);

      setIsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="justify-start text-sm leading-none font-medium text-neutral-200">
                {t('login.email')}
              </FormLabel>
              <FormControl>
                <Input
                  autoComplete="email"
                  type="email"
                  aria-invalid={form.formState.errors.email ? true : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="justify-start text-sm leading-none font-medium text-neutral-200">
                {t('login.password')}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  autoComplete="current-password"
                  aria-invalid={form.formState.errors.password ? true : undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-7">
          <Link
            href="/forgot-password"
            className="text-grey-900 ml-auto w-fit text-right text-sm leading-none font-medium dark:text-neutral-100"
          >
            {t('login.forgotPassword')}
          </Link>

          <div className="flex flex-col gap-7">
            <Button className="gap-6" type="submit" disabled={isLoading}>
              {isLoading ? t('common.loading.signingIn') : t('login.title')}
            </Button>

            {process.env.ENVIRONMENT_NAME !== 'prod' && <DontHaveAnAccountYet />}
          </div>
        </div>
      </form>
    </Form>
  );
}
